import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivityDefinition, Bundle, ChargeItemDefinition, CodeSystem } from 'fhir/r4';
import { EMPTY, Observable, catchError, expand, forkJoin, map, of, reduce, switchMap, take } from 'rxjs';
import { backendEndPointToken, serviceRequestCategoriesToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { StateService } from '../shared/state.service';
import { UtilityService } from '../shared/utility.service';
import { DisableCountParam } from '../loading.interceptor';

export type ServiceDetailsRow = {
  code: string;
  display: string;
  categoryDisplay: string;
  active: boolean;
  itemsRequiredCount: number;
  totalPriceDisplay: string;
  hasActivityDefinition: boolean;
  hasChargeItemDefinition: boolean;
  publisher?: string;
};

export type ServiceDetailsLoadResult = {
  configured: ServiceDetailsRow[];
  notConfigured: ServiceDetailsRow[];
};

@Injectable({
  providedIn: 'root'
})
export class ServiceDetailsService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly terminologyCanonicalUrlBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly serviceCategories = inject(serviceRequestCategoriesToken);
  private readonly stateService = inject(StateService);
  private readonly utilityService = inject(UtilityService);
  private readonly chargeItemDefinitionPageCap = 25;

  loadServiceDetails(): Observable<ServiceDetailsLoadResult> {
    return forkJoin({
      codeSystem: this.loadServiceRequestCodeSystem(),
      activities: this.loadActivityDefinitions(),
      scopedActivities: this.loadItemsUsageActivityDefinitions(),
      charges: this.loadChargeItemDefinitions(),
    }).pipe(
      map(({ codeSystem, activities, scopedActivities, charges }) => {
        const activeActivities = [...(activities || []), ...(scopedActivities || [])]
          .filter((a) => (a as any)?.status === 'active');
        const activeCharges = (charges || []).filter((c) => (c as any)?.status === 'active');

        const chargeByCode = new Map<string, ChargeItemDefinition>();
        activeCharges.forEach((c) => {
          const chargeCode = c?.code?.coding?.[0]?.code;
          if (chargeCode) {
            chargeByCode.set(String(chargeCode), c);
          }
        });

        const activityByCode = new Map<string, ActivityDefinition>();
        activeActivities.forEach((ad) => {
          const code = ((ad as any)?.code?.coding?.[0]?.code || '').toString().trim();
          if (!code) return;
          if (!this.isItemsUsageComputationActivity(ad)) return;
          if (!activityByCode.has(code)) {
            activityByCode.set(code, ad);
          }
        });

        const allRows: ServiceDetailsRow[] = (codeSystem || []).map((c) => {
          const code = (c.code || '').toString();
          const activity = activityByCode.get(code);
          const charge = chargeByCode.get(code);
          return {
            code,
            display: c.display || code,
            categoryDisplay: this.getCategoryDisplay(c.categoryCode),
            active: c.active,
            itemsRequiredCount: activity ? this.countItemsRequired(activity) : 0,
            totalPriceDisplay: this.formatSellingPrice(charge),
            hasActivityDefinition: !!activity,
            hasChargeItemDefinition: !!charge,
            publisher: activity?.publisher,
          } satisfies ServiceDetailsRow;
        });

        return {
          configured: allRows.filter((r) => r.hasActivityDefinition),
          notConfigured: allRows.filter((r) => !r.hasActivityDefinition),
        } satisfies ServiceDetailsLoadResult;
      })
    );
  }

  private loadServiceRequestCodeSystem(): Observable<ServiceCodeSystemConcept[]> {
    const canonicalUrl = `${this.terminologyCanonicalUrlBase}/CodeSystem/ServiceRequest/ServiceRequestCodes`;
    const searchUrl = `${this.backendUrl}/CodeSystem?url=${encodeURIComponent(canonicalUrl)}&_count=1`;

    const mapConceptsFromCodeSystem = (cs: CodeSystem | null | undefined): ServiceCodeSystemConcept[] => {
      const concepts: any[] = Array.isArray((cs as any)?.concept) ? ((cs as any).concept as any[]) : [];
      return concepts
        .map((concept) => {
          const code = (concept?.code || '').toString().trim();
          if (!code) return null;

          const display = (concept?.display || '').toString().trim() || code;
          const props: any[] = Array.isArray(concept?.property) ? concept.property : [];
          const categoryProp = props.find((p: any) => p?.code === 'category');
          const activeProp = props.find((p: any) => p?.code === 'active');

          const categoryCode = (categoryProp?.valueString || categoryProp?.valueCode || '').toString().trim() || null;
          const active = activeProp?.valueBoolean !== false;

          return {
            code,
            display,
            categoryCode,
            active,
          } satisfies ServiceCodeSystemConcept;
        })
        .filter(Boolean) as ServiceCodeSystemConcept[];
    };

    return this.stateService.orgWideResources.codeSystems.pipe(
      take(1),
      switchMap((cached) => {
        const cachedResource = (cached || [])
          .map((x) => x.actualResource)
          .find((cs) => ((cs as any)?.url || '').toString() === canonicalUrl);

        const cachedConcepts = mapConceptsFromCodeSystem(cachedResource);
        if (cachedConcepts.length) {
          return of(cachedConcepts);
        }

        return this.http.get<Bundle>(searchUrl).pipe(
          take(1),
          map((bundle: any) => {
            const cs = (bundle?.entry?.[0]?.resource || null) as CodeSystem | null;

            if (cs) {
              const nextList = Array.isArray(cached) ? [...cached] : [];
              const id = (cs as any)?.id ? String((cs as any).id) : null;
              const idx = id
                ? nextList.findIndex((x) => (x?.actualResource as any)?.id && String((x.actualResource as any).id) === id)
                : nextList.findIndex((x) => ((x?.actualResource as any)?.url || '').toString() === canonicalUrl);

              const wrapper = {
                actualResource: cs,
                referenceId: id ? `CodeSystem/${id}` : null,
                savedStatus: 'saved' as const,
              };

              if (idx >= 0) {
                nextList[idx] = wrapper;
              } else {
                nextList.push(wrapper);
              }

              this.stateService.orgWideResources.codeSystems.next(nextList);
            }

            return mapConceptsFromCodeSystem(cs);
          }),
          catchError(() => of([]))
        );
      })
    );
  }

  private loadChargeItemDefinitions(): Observable<ChargeItemDefinition[]> {
    // return this.stateService.orgWideResources.chargeItemDefinitions.pipe(
    //   take(1),
    //   switchMap((defs) => {
    //     if (defs && defs.length) return of(defs.map((d) => d.actualResource));

    //     return this.fetchAllChargeItemDefinitions().pipe(
    //       map((resources) => {
    //         this.stateService.orgWideResources.chargeItemDefinitions.next(
    //           resources.map((d) => ({
    //             actualResource: d,
    //             referenceId: d?.id ? `ChargeItemDefinition/${d.id}` : null,
    //             savedStatus: 'saved',
    //           }))
    //         );
    //         return resources;
    //       })
    //     );
    //   })
    // );
      return this.utilityService.fetchAll<ChargeItemDefinition>({
                resourceType: 'ChargeItemDefinition',
                queries: {
                  status: 'active',
                },
                checkOrgWideState: true,
                updateOrgWideState: true,
                localFilters: [
                 { key: 'status', value: 'active' }
                ]
              })
  }

  private loadActivityDefinitions(): Observable<ActivityDefinition[]> {
    // return this.stateService.orgWideResources.activityDefinitions.pipe(
    //   take(1),
    //   switchMap((defs) => {
    //     if (defs && defs.length) return of(defs.map((d) => d.actualResource));

    //     return this.http.get<Bundle>(`${this.backendUrl}/ActivityDefinition?status=active&_count=500`).pipe(
    //       take(1),
    //       map((bundle) => (bundle.entry || []).map((e: any) => e.resource as ActivityDefinition)),
    //       map((resources) => {
    //         this.stateService.orgWideResources.activityDefinitions.next(
    //           resources.map((d) => ({
    //             actualResource: d,
    //             referenceId: d?.id ? `ActivityDefinition/${d.id}` : null,
    //             savedStatus: 'saved',
    //           }))
    //         );
    //         return resources;
    //       })
    //     );
    //   })
    // );
    return this.utilityService.fetchAll<ActivityDefinition>({
                resourceType: 'ActivityDefinition',
                queries: {
                  status: 'active',
                },
                checkOrgWideState: true,
                updateOrgWideState: true,
                localFilters: [
                 { key: 'status', value: 'active' }
                ]
              })
  }

  private fetchAllChargeItemDefinitions(): Observable<ChargeItemDefinition[]> {
    const context = new HttpContext().set(DisableCountParam, true);
    const initialUrl = `${this.backendUrl}/ChargeItemDefinition?status=active`;

    return this.http.get<Bundle>(initialUrl, { context }).pipe(
      expand((bundle, index) => {
        if (index + 1 >= this.chargeItemDefinitionPageCap) return EMPTY;
        const nextLink = this.findNextLinkUrl(bundle);
        return nextLink ? this.http.get<Bundle>(nextLink, { context }) : EMPTY;
      }),
      map((bundle) => (bundle?.entry || []).map((e: any) => e.resource as ChargeItemDefinition)),
      reduce((acc, page) => acc.concat(page), [] as ChargeItemDefinition[]),
      catchError(() => of([]))
    );
  }

  private loadItemsUsageActivityDefinitions(): Observable<ActivityDefinition[]> {
    // ActivityDefinition does not define a `code` search parameter per HL7 FHIR R4, so fetch
    // every active "items usage computation" topic once and match locally.
    return this.utilityService
      .threeTierQueryAndSyncToStateOnce$<ActivityDefinition>({
        resourceType: 'ActivityDefinition',
        scope: 'orgWide',
        context: new HttpContext().set(DisableCountParam, true),
        urlSearchObject: {
          status: 'active',
          topic: 'items usage computation',
        },
        querySearchObject: [
          { key: 'status', value: 'active' },
          { key: 'topic.text', value: 'items usage computation' },
        ],
        tier1FollowNextLinks: true,
        tier1MaxPages: 100,
      })
      .pipe(
        take(1),
        map((list) => (Array.isArray(list) ? list : [])),
        catchError(() => of([]))
      );
  }

  private getCategoryDisplay(categoryCode: string | null): string {
    if (!categoryCode) return '';
    const match = (this.serviceCategories || []).find((c) => c?.code === categoryCode);
    return match?.display || categoryCode;
  }

  private isItemsUsageComputationActivity(activity: ActivityDefinition): boolean {
    const topics: any[] = Array.isArray((activity as any)?.topic) ? ((activity as any).topic as any[]) : [];
    return topics.some((t: any) => (t?.text || '').toString().trim().toLowerCase() === 'items usage computation');
  }

  private countItemsRequired(activity: ActivityDefinition): number {
    const exts = Array.isArray((activity as any)?.extension) ? (activity as any).extension : [];
    return exts.filter((e: any) => e?.url === 'http://hl7.org/fhir/StructureDefinition/items-required').length;
  }

  private formatSellingPrice(charge?: ChargeItemDefinition): string {
    if (!charge) return '';
    const result = this.utilityService.seperateTotalPriceComponents(charge);
    if (!result) return '';

    const currency = result.currency || 'NGN';
    const total = Number(result.totalSellingPrice || 0);

    return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(total);
  }

  private findNextLinkUrl(bundle: Bundle | null | undefined): string | null {
    const links: any[] = Array.isArray(bundle?.link) ? (bundle as any).link : [];
    const next = links.find((link: any) => (link?.relation || '').toString() === 'next');
    const url = (next?.url || '').toString().trim();
    return url || null;
  }
}

type ServiceCodeSystemConcept = {
  code: string;
  display: string;
  categoryCode: string | null;
  active: boolean;
};
